import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AuthService } from "../../../auth/auth.service";
import { AutomapperService } from "../../../core/automapper/automapper.service";
import { BASE_API_URL } from "../../../core/environment.tokens";
import { CodingReviewMode } from "../project/project-config/coding-review-mode.enum";
import { AttributesAndChaseModel } from "./attributes-and-chase.model";
import { CODING_REVIEW_MODE, ENABLE_PROJECT_FOR_QUALITY_30, ENABLE_RISK_20 } from "./chase-detail/chase-detail-chart/attributes";

@Injectable()
export class ChasePageUrlService implements CanActivate {

  isRiskType30 = false;
  isRiskType21 = false;
  isRiskType20 = false;
  isRiskType22 = false;
  isQualityType30 = false;

  constructor(
    @Inject(BASE_API_URL) private readonly baseApiUrl: string,
    private router: Router,
    private http: HttpClient,
    private readonly automapper: AutomapperService,
    private readonly authService: AuthService
  ) { }

  get riskVersion2Enabled(): boolean {
    return this.isRiskType21 || this.isRiskType22 || this.isRiskType20;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const userId = this.authService.userId;
    const isV2Chase = state.url.includes("chasev2");
    const chaseId = +route.params.chaseGd;

    const attributeIdsAsCsv = `${ENABLE_RISK_20.attributeId},
      ${CODING_REVIEW_MODE.attributeId},
      ${ENABLE_PROJECT_FOR_QUALITY_30.attributeId}`;

    return new Observable(subscribe => {
      if (isV2Chase) {
        subscribe.next(true);
        subscribe.complete();
        return;
      }

      this.getAttributesAndChase(chaseId, userId, attributeIdsAsCsv).subscribe(attributes => {
        this.initializeAttributeValuesToVariables(attributes);
        if (this.isRiskType30 || this.isQualityType30) {
          const referrerUrl = document.referrer;
          // get the relevant part of the referrer URL
          const prevUrl = referrerUrl ? new URL(referrerUrl).pathname + new URL(referrerUrl).search : "";
          // Redirect to the risk v3 chase page with returnUrl
          // returnUrl is used to navigate back to the original page after viewing the chase
          // This is a full page load out of the SPA, so mark it as intentional to keep the
          // beforeunload handler from treating it as the last tab closing and logging the user out.
          this.authService.beginInternalNavigation();
          window.location.href = `/app/chases/${chaseId}?returnUrl=${encodeURIComponent(prevUrl)}`;
          subscribe.next(false);
          subscribe.complete();
        } else if (this.riskVersion2Enabled) {
          this.router.navigateByUrl(`/members/chasev2/${chaseId}`);
          subscribe.next(false);
          subscribe.complete();
        } else {
          subscribe.next(true);
          subscribe.complete();
        }
        this.resetAttribute();

      });
    });
  }

  private initializeAttributeValuesToVariables(data: any): void {
    const attributes = data.attributes || [];
    const measureCode = data.measureCode || "";

    attributes.forEach(attribute => {
      if (attribute.attributeId === CODING_REVIEW_MODE.attributeId) {
        const value = attribute.attributeValue;
        this.isRiskType21 = value === CodingReviewMode.DIAGNOSIS;
        this.isRiskType30 = value === CodingReviewMode.DIAGNOSIS_V3;
        this.isRiskType22 = value === CodingReviewMode.DIAGNOSIS_V2_2;
      }

      if (attribute.attributeId === ENABLE_RISK_20.attributeId && (measureCode === "HCC" || measureCode === "HST" || measureCode === "MCD")) {
        const value = attribute.attributeValue;
        this.isRiskType20 = Number(value) === 1;
      }

      if (attribute.attributeId === ENABLE_PROJECT_FOR_QUALITY_30.attributeId) {
        const value = attribute.attributeValue;
        this.isQualityType30 = Number(value) === 1;
      }

    });
  }

  private resetAttribute(): void {
    this.isRiskType30 = false;
    this.isRiskType21 = false;
    this.isRiskType20 = false;
    this.isRiskType22 = false;
    this.isQualityType30 = false;
  }

  private getAttributesAndChase(chaseId: number, userId: number, attributeIdsAsCsv: string): Observable<AttributesAndChaseModel> {
    const url = `${this.baseApiUrl}projectconfiguration/data/attributesAndChase?chaseId=${chaseId}&userId=${userId}&attributeIdsAsCsv=${attributeIdsAsCsv}`;

    return this.http.get(url).pipe(
      map(this.automapper.curry("default", "AttributesAndChase"))
    );
  }
 }
